/* eslint-disable no-unused-vars */
import { useState, useMemo, React } from 'react';
import { useTranslation } from '@app/config/i18n';
import { useSubmissionForm } from '@app/hooks/useSubmissionForm';
import { APPLICATION_STATUS } from '@app/constants';
import moment from 'moment';
import Typography from '@app/components/atoms/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Link from '@app/components/atoms/Link';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@app/components/atoms/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const useSubmissionsListTable = ({
  opportunity,
  submissionDataArray,
  navigationQuery,
}) => {
  const { t } = useTranslation(['common']);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [selected, setSelected] = useState({});


  // Get columns configuration
  const { columns, handleToggleColumn } = useSubmissionForm({
    opportunity,
    isOrganizer: true,
  });

  // Process submission data for the table
  const tableRowsData = useMemo(() => {
    if (!submissionDataArray?.length) return [];

    return submissionDataArray.map((submission) => {
      const rowData = {
        id: submission.id,
        status: submission.status,
        payment_status: submission.payment_status,
        registration_status: submission.registration_status,
        created_at: submission.created_at,
        originalData: submission,
      };

      // Add field values from elements
      if (submission.elements?.length > 0) {
        submission.elements.forEach((element) => {
          if (element.field_id) {
            rowData[element.field_id] = element.value;
          }
        });
      }

      return rowData;
    });
  }, [submissionDataArray]);

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = {};
      tableRowsData.forEach((row) => {
        newSelected[row.id] = row.originalData;
      });
      setSelected(newSelected);
      return;
    }
    setSelected({});
  };

  const handleRowClick = (event, row) => {
    const newSelected = { ...selected };

    if (newSelected[row.id]) {
      // Item is selected, remove it
      delete newSelected[row.id];
    } else {
      // Item is not selected, add it
      newSelected[row.id] = row.originalData;
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => Boolean(selected[id]);

  // Render cell content based on field type
  const renderCellContent = (row, headCell) => {
    if (headCell.id === 'status') {
      let color = 'primary';
      if (
        row.status === APPLICATION_STATUS.REVIEWED ||
        row.status === APPLICATION_STATUS.UNDER_REVIEW
      ) {
        color = '#FAB32B';
      } else if (row.status === APPLICATION_STATUS.ACCEPTED) {
        color = '#67BF4E';
      } else if (row.status === APPLICATION_STATUS.REJECTED) {
        color = '#E75055';
      }

      return (
        <Typography variant="caption" color={color}>
          {row.status === APPLICATION_STATUS.UNDER_REVIEW ? 'Under Review' : row.status}
        </Typography>
      );
    }

    if (headCell.id === 'payment_status') {
      const approvalType = row.originalData?.opportunity?.approval_type;
      const regStatus = row.status;
      const paymentStatus = row.payment_status;

      if (!paymentStatus) {
        return (
          <Typography variant="caption" color="secondary">
            {t('common:FREE')}
          </Typography>
        );
      }

      if (approvalType === 'AUTO' || approvalType === 'ON_REVIEW') {
        return (
          <Typography variant="caption" color="secondary">
            {paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
          </Typography>
        );
      }

      return (
        <Typography variant="caption" color="secondary">
          {t('common:NO ACTION')}
        </Typography>
      );
    }

     if (headCell.id === 'registration_status') {
        let color = 'primary';
        if (
          row.registration_status === 'registered'
        ) {
          color = '#67BF4E';
        } else {
          color = 'primary';
        }

        return (
          <Typography variant="caption" color={color}>
            {row.registration_status === 'registered' ? 'Registered' : 'Unregistered'}
          </Typography>
        );
      }

    if (headCell.id === 'created_at') {
      return (
        <Typography variant="caption" color="secondary">
          {moment(row.created_at).format('DD MMM YYYY').toUpperCase()}
        </Typography>
      );
    }

    // Find the original field data to determine type
    const field = opportunity?.submission_form?.fields?.find((f) => f.id === headCell.id);
    const fieldType = field?.type;

    // For custom fields
    if (row.originalData?.elements) {
      const element = row.originalData.elements.find((el) => el.field_id === headCell.id);

      if (element) {
        if (fieldType === 'title' || fieldType === 'mandatory_title') {
          const value = element.value || '';
          return value.length > 40 ? (
            <Tooltip title={value} arrow placement="top">
              <div>
                <Link
                  style={{ fontSize: '14px' }}
                  color="secondary"
                  href="view_submission"
                  params={{ slug: opportunity.slug, id: row.id }}
                  query={navigationQuery}
                >
                  {value.substring(0, 41)}...
                </Link>
              </div>
            </Tooltip>
          ) : (
            <Link
              style={{ fontSize: '14px' }}
              color="secondary"
              href="view_submission"
              params={{ slug: opportunity.slug, id: row.id }}
              query={navigationQuery}
            >
              {value}
            </Link>
          );
        }

        if (fieldType === 'abstract' || fieldType === 'mandatory_abstract') {
          return element.value ? <CheckIcon color="secondary" /> : <CloseIcon color="secondary" />;
        }

        if (fieldType === 'paper' || fieldType === 'mandatory_paper') {
          return element.files?.length > 0 ? (
            <CheckIcon color="secondary" />
          ) : (
            <CloseIcon color="secondary" />
          );
        }

        if (fieldType === 'mandatory_authors' && element.child_elements) {
          return (
            <Typography variant="caption" color="secondary">
              {element.child_elements[0] ? element.child_elements[0][0]?.value : ''}
              {element.child_elements.length > 1 && ` +${element.child_elements.length - 1}`}
            </Typography>
          );
        }

        return <Typography variant="caption">{element.value}</Typography>;
      }
    }

    return <Typography variant="caption">{row[headCell.id] || ''}</Typography>;
  };

  // Add this function before the main component
  const renderViewAction = (row) => {
    const emails = row.originalData?.user?.email ? [row.originalData.user.email] : [];

    return (
      <Tooltip title={t('common:View submission')}>
        <Link
          href="view_submission"
          params={{ slug: opportunity.slug, id: row.id }}
          query={navigationQuery}
        >
          <IconButton size="small" sx={{ color: 'primary.main' }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Link>
      </Tooltip>
    );
  };

  return {
    // State
    order,
    orderBy,
    selected,
    columns,
    tableRowsData,

    // Data

    // Handlers
    handleRequestSort,
    handleSelectAllClick,
    handleRowClick,
    handleToggleColumn,
    renderViewAction,
    // Utilities
    isSelected,
    renderCellContent,
  };
};
